import { useCallback, useEffect, useRef, useState } from 'react';

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 8;

// Owns waveform canvas drawing + drag/zoom/snap state so
// WaveformTrimmer.jsx only has to render markup and wire up event handlers.
export function useWaveformTrimmer({ peaks, duration, trimStart, trimEnd, currentTime, isPlaying, onTrimChange, lockLength = false }) {
  const canvasRef = useRef(null);
  const scrollRef = useRef(null); // outer, fixed-width, horizontally scrollable
  const contentRef = useRef(null); // inner, actual (possibly zoomed) coordinate space
  const dragRef = useRef(null); // { mode: 'start' | 'end' | 'move', ... } | null
  const [zoom, setZoom] = useState(1);
  const [snapEnabled, setSnapEnabled] = useState(true);

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !peaks) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const startX = (trimStart / duration) * w;
    const endX = (trimEnd / duration) * w;
    const mid = h / 2;
    const barWidth = w / peaks.length;

    peaks.forEach((p, i) => {
      const x = i * barWidth;
      const barH = Math.max(1, (p.max - p.min) * mid);
      const inTrim = x >= startX && x <= endX;
      ctx.fillStyle = inTrim ? '#ea580c' : '#d4d4d4';
      ctx.fillRect(x, mid - barH / 2, Math.max(1, barWidth - 0.5), barH);
    });

    ctx.fillStyle = 'rgba(23,21,26,0.45)';
    ctx.fillRect(0, 0, startX, h);
    ctx.fillRect(endX, 0, w - endX, h);

    // Faint tick at every whole second when snapping is on, so the snap
    // points the handles will jump to are visible while dragging.
    if (snapEnabled && duration > 0) {
      ctx.fillStyle = 'rgba(23,21,26,0.18)';
      const pxPerSecond = w / duration;
      if (pxPerSecond >= 6) {
        for (let sec = 0; sec <= Math.floor(duration); sec++) {
          const x = sec * pxPerSecond;
          ctx.fillRect(x, 0, 1, h);
        }
      }
    }

    if (isPlaying) {
      const playX = ((trimStart + currentTime) / duration) * w;
      ctx.strokeStyle = '#171518';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(playX, 0);
      ctx.lineTo(playX, h);
      ctx.stroke();
    }
  }, [peaks, duration, trimStart, trimEnd, currentTime, isPlaying, zoom, snapEnabled]);

  useEffect(() => { drawWaveform(); }, [drawWaveform]);

  useEffect(() => {
    const handleResize = () => drawWaveform();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawWaveform]);

  // timeFromClientX must measure against the (possibly zoomed) content
  // element, not the fixed-width outer scroll container -- that's the
  // coordinate space the waveform, trim handles, and playhead all share.
  const timeFromClientX = useCallback((clientX) => {
    const rect = contentRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const onPointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag) return;

    if (drag.mode === 'move') {
      const rect = contentRef.current.getBoundingClientRect();
      const deltaTime = ((e.clientX - drag.startX) / rect.width) * duration;
      const regionLength = drag.origEnd - drag.origStart;
      let newStart = drag.origStart + deltaTime;
      if (snapEnabled) newStart = Math.round(newStart);
      newStart = Math.max(0, Math.min(newStart, duration - regionLength));
      onTrimChange(newStart, newStart + regionLength);
      return;
    }

    let t = timeFromClientX(e.clientX);
    const minGap = snapEnabled ? 1 : 0.2;
    if (snapEnabled) t = Math.round(t);
    if (drag.mode === 'start') {
      onTrimChange(Math.min(t, trimEnd - minGap), trimEnd);
    } else {
      onTrimChange(trimStart, Math.max(t, trimStart + minGap));
    }
  }, [timeFromClientX, trimStart, trimEnd, onTrimChange, snapEnabled, duration]);

  const onPointerUp = useCallback(() => { dragRef.current = null; }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  // Trackpad pinch gestures fire as wheel events with ctrlKey (or metaKey
  // on some browsers) set -- treat those as zoom, leave plain scroll/swipe
  // alone so it still pans the (now horizontally scrollable) waveform.
  const handleWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.5 : 0.5;
    setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, +(z + delta).toFixed(2))));
  };

  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, Math.round(z + 1)));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, Math.round(z - 1)));

  // Edge handles ('start'/'end') resize the trim window from the pointer's
  // absolute position; the 'move' handle instead tracks the delta from
  // where the drag began, so it shifts the whole window without resizing it.
  const startDrag = (which) => (e) => {
    if (which !== 'move' && lockLength) return;
    if (which === 'move') {
      dragRef.current = { mode: 'move', startX: e.clientX, origStart: trimStart, origEnd: trimEnd };
    } else {
      dragRef.current = { mode: which };
    }
  };

  const startPct = duration ? (trimStart / duration) * 100 : 0;
  const endPct = duration ? (trimEnd / duration) * 100 : 100;

  return {
    canvasRef, scrollRef, contentRef,
    zoom, snapEnabled, setSnapEnabled,
    handleWheel, zoomIn, zoomOut, startDrag,
    startPct, endPct,
  };
}
